/**
 * How much of the exam each camera actually recorded.
 *
 * This is the number that would have caught the attempt which uploaded 4:17 of
 * a 12:11 sitting and looked completely normal on every other screen: the
 * recordings played, they just stopped early, and nothing anywhere said what
 * fraction of the exam they covered. So the figure is printed as text next to
 * the bar rather than left to the bar alone — a reader who is colour-blind, or
 * who is skimming a table at a glance, still gets the actual percentage.
 *
 * Low coverage is not evidence of anything. It is the opposite: a stretch of
 * the exam about which the recording can say nothing either way, which is
 * precisely why a human has to look at the rest of the attempt.
 */
import { useId } from "react";
import type { AttemptCoverage, CoverageVerdict, SourceCoverage } from "@exam/shared";
import { cn } from "@/lib/cn";
import { formatClock, formatPercent } from "../lib/format";

/**
 * Verdict colours come straight from the exam palette: complete reads as the
 * answered green, partial as the amber already used for "marked for review",
 * and both poor and none as the violation red. Nothing here is a new colour,
 * and nothing here means the same as a proctoring violation — the red says
 * "this recording cannot answer the question", not "this student did something".
 */
const VERDICT_TONE: Record<CoverageVerdict, string> = {
  complete: "text-answered",
  partial: "text-review",
  poor: "text-violation",
  none: "text-violation",
};

/**
 * Hue is never the only carrier of meaning: the two verdicts that need action
 * are also hatched, so they stay distinguishable in greyscale and to a reader
 * who cannot separate the green from the amber.
 */
const HATCHED: Record<CoverageVerdict, boolean> = {
  complete: false,
  partial: false,
  poor: true,
  none: true,
};

const BAR_WIDTH = 56;
const BAR_HEIGHT = 6;

export function CoverageBar({
  coverage,
  className,
}: {
  /** Null when the attempt never actually ran, so there is no window to cover. */
  coverage: AttemptCoverage | null;
  className?: string;
}) {
  if (!coverage || coverage.sources.length === 0) {
    return <span className="text-2xs text-ink-faint">—</span>;
  }

  return (
    <ul className={cn("space-y-0.5", className)}>
      {coverage.sources.map((source) => (
        <SourceRow key={source.source} source={source} />
      ))}
    </ul>
  );
}

function SourceRow({ source }: { source: SourceCoverage }) {
  const hatchId = useId();
  const ratio = source.coverage ?? 0;
  const tone = VERDICT_TONE[source.verdict];

  // A source that uploaded nothing at all is the loudest case on this screen,
  // so it says so in words. "0%" alone reads like a rounding artefact; "no
  // footage" cannot be misread as a camera that merely dropped a few seconds.
  const noFootage = source.verdict === "none";

  const gapNote =
    source.longest_gap_ms > 0
      ? `longest gap ${formatClock(source.longest_gap_ms)}`
      : "no gaps";

  return (
    <li
      className="flex items-center gap-1.5 leading-none"
      title={`${source.source}: ${formatPercent(ratio)} of the exam recorded, ${gapNote}`}
    >
      <span className="w-12 shrink-0 truncate text-2xs text-ink-faint">
        {source.source}
      </span>
      <svg
        width={BAR_WIDTH}
        height={BAR_HEIGHT}
        viewBox={`0 0 ${BAR_WIDTH} ${BAR_HEIGHT}`}
        className={cn("shrink-0", tone)}
        role="img"
        aria-label={`${source.source} recorded ${formatPercent(ratio)} of the exam`}
      >
        {HATCHED[source.verdict] ? (
          <defs>
            <pattern
              id={hatchId}
              width="4"
              height="4"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="4" height="4" fill="currentColor" opacity="0.25" />
              <rect width="2" height="4" fill="currentColor" />
            </pattern>
          </defs>
        ) : null}
        {/* The track is drawn first so an empty bar still reads as a bar with
            nothing in it, rather than as a missing element. */}
        <rect
          x="0"
          y="0"
          width={BAR_WIDTH}
          height={BAR_HEIGHT}
          rx="3"
          className="fill-border"
        />
        {ratio > 0 ? (
          <rect
            x="0"
            y="0"
            width={Math.max(2, ratio * BAR_WIDTH)}
            height={BAR_HEIGHT}
            rx="3"
            fill={HATCHED[source.verdict] ? `url(#${hatchId})` : "currentColor"}
          />
        ) : null}
      </svg>
      <span className={cn("tabular w-8 shrink-0 text-right text-2xs", tone)}>
        {formatPercent(ratio)}
      </span>
      {noFootage ? (
        <span className="text-2xs text-violation">no footage</span>
      ) : null}
    </li>
  );
}
